import { useEffect, useState } from "react";

type Film = {
  title: string;
  durationSeconds: number;
  url: string;
  poster: string;
  captions: string;
};

/** The video manifest is not part of the Vite build; tools/docs/publish-video-assets.mjs writes it into the
 *  release and records its URL in this meta tag. No tag means this release ships no videos, so the section
 *  (and its nav links) are omitted instead of requesting a manifest that would 404. */
export function productVideosManifestUrl(
  doc: Pick<Document, "querySelector"> | undefined = typeof document === "undefined"
    ? undefined
    : document,
): string | null {
  const url = doc
    ?.querySelector('meta[name="provenance-product-videos"]')
    ?.getAttribute("content")
    ?.trim();
  return url?.startsWith("/") && !url.startsWith("//") ? url : null;
}

export function ProductVideos() {
  const manifestUrl = productVideosManifestUrl();
  const [films, setFilms] = useState<Film[]>([]);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!manifestUrl) return;
    const controller = new AbortController();
    fetch(manifestUrl, { signal: controller.signal })
      .then((r) => {
        if (!r.ok) throw new Error("Unavailable");
        return r.json();
      })
      .then((data: unknown) => {
        if (!Array.isArray(data)) throw new Error("Malformed manifest");
        setFilms(data as Film[]);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setFailed(true);
      });
    return () => controller.abort();
  }, [manifestUrl]);
  if (!manifestUrl) return null;
  return (
    <section className="buyer-section" id="videos" aria-labelledby="product-videos-heading">
      <p className="story-kicker">See the workflows</p>
      <h2 id="product-videos-heading">Ten short product demonstrations</h2>
      <p>
        Explore scanning, evidence, and review in about a minute each. These recordings show an
        earlier product version; screens and available features may differ from the current console.
      </p>
      {failed && (
        <p role="status">The video library could not load. Please refresh to try again.</p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,320px),1fr))",
          gap: 24,
        }}
      >
        {films.map((film, i) => (
          <article key={film.url}>
            <h3>
              {i + 1}. {film.title}
            </h3>
            <p>{Math.round(film.durationSeconds)} seconds</p>
            <video
              controls
              preload="none"
              playsInline
              poster={film.poster}
              aria-label={film.title}
              style={{
                width: "100%",
                aspectRatio: "16/9",
                background: "#100b1c",
                borderRadius: 12,
              }}
            >
              <source src={film.url} type="video/mp4" />
              <track kind="captions" src={film.captions} srcLang="en" label="English" default />
              Your browser does not support this video.
            </video>
          </article>
        ))}
      </div>
    </section>
  );
}
